import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { fmtRoDate } from "../lib/dateFmt";
import { triggerUrgentAlert, hasBeenAlerted, markAlerted, requestNotifPermissionOnce } from "../lib/urgentAlert";

const PRIORITY_COLORS = {
  urgent: "bg-red-600 text-white",
  high: "bg-red-100 text-red-800 border border-red-300",
  normal: "bg-amber-100 text-amber-800 border border-amber-300",
  low: "bg-zinc-100 text-zinc-700 border border-zinc-300",
};
const PRIORITY_LABEL = { urgent: "⚡ URGENT", high: "🔴 Prioritate mare", normal: "🟡 Normal", low: "🟢 Scăzut" };

/** Banner roșu cu task-urile primite (`/api/tasks?status=pending`).
 *  Vizibil pe Dashboard.jsx (admin) și EmployeeDashboard.jsx (angajat). */
export default function AssignedTasksBanner() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmTask, setConfirmTask] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tasks", { params: { status: "pending", mine: true } });
      const list = data || [];
      setTasks(list);
      // Declanșează sunet + vibrație pentru task-uri URGENTE noi (necitite anterior)
      try {
        const newUrgent = list.filter(
          (t) => (t.priority || "").toLowerCase() === "urgent" && !hasBeenAlerted(t.id),
        );
        if (newUrgent.length > 0) {
          const first = newUrgent[0];
          const res = triggerUrgentAlert({
            title: newUrgent.length === 1
              ? `⚡ Task URGENT: ${first.title || "(fără titlu)"}`
              : `⚡ ${newUrgent.length} task-uri URGENTE noi`,
            body: newUrgent.length === 1
              ? (first.description || `De la ${first.created_by_name || "Admin"}`)
              : newUrgent.map((t) => `• ${t.title}`).slice(0, 5).join("\n"),
          });
          newUrgent.forEach((t) => markAlerted(t.id));
          if (res.sound || res.vibration) {
            toast.error(
              newUrgent.length === 1
                ? `⚡ Task URGENT nou: ${first.title}`
                : `⚡ ${newUrgent.length} task-uri URGENTE noi!`,
              { duration: 8000 },
            );
          }
        }
      } catch { /* noop */ }
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // Cere permisiunea de notificări OS o singură dată (idempotent)
    requestNotifPermissionOnce().catch(() => {});
    load();
    // Poll la fiecare 60s pentru a prinde task-uri urgente noi fără reload.
    const id = setInterval(load, 60 * 1000);
    // Re-check când fereastra redevine vizibilă (user revine la tab/aplicație)
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sorted = useMemo(() => {
    const order = { urgent: 0, high: 1, normal: 2, low: 3 };
    return [...tasks].sort((a, b) => {
      const da = order[a.priority || "normal"] ?? 9;
      const db = order[b.priority || "normal"] ?? 9;
      if (da !== db) return da - db;
      const ad = a.due_date || "9999-12-31";
      const bd = b.due_date || "9999-12-31";
      return ad.localeCompare(bd);
    });
  }, [tasks]);

  const markDone = async (t) => {
    try {
      await api.patch(`/tasks/${t.id}/complete`);
      toast.success(`Task „${t.title}" marcat ca executat`);
      setConfirmTask(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la marcare");
    }
  };

  if (loading || tasks.length === 0) return null;
  const hasUrgent = sorted.some((t) => t.priority === "urgent" || t.priority === "high");

  return (
    <div
      className={`card-flat p-4 mb-6 border-l-8 ${hasUrgent ? "border-l-red-600 bg-red-50" : "border-l-amber-500 bg-amber-50"}`}
      data-testid="assigned-tasks-banner"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className={`shrink-0 mt-0.5 ${hasUrgent ? "text-red-600" : "text-amber-600"}`} size={24} />
        <div className="flex-1">
          <div className={`font-display font-bold text-lg ${hasUrgent ? "text-red-700" : "text-amber-800"}`}>
            Ai {tasks.length} task{tasks.length > 1 ? "-uri" : ""} de executat
          </div>
          <p className="text-xs text-zinc-600 mt-0.5">Citește titlul + descrierea, apoi apasă „Executat" după ce termini fiecare task.</p>
          <div className="mt-3 space-y-2">
            {sorted.map((t) => (
              <div key={t.id} className={`rounded-md p-3 flex flex-wrap items-start gap-3 shadow-sm ${t.source === "auto_missing_report" ? "bg-red-50 border-2 border-red-400" : "bg-white"}`} data-testid={`task-${t.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    {t.source === "auto_missing_report" && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold bg-red-600 text-white animate-pulse" data-testid={`missing-report-badge-${t.id}`}>
                        📄 RAPORT FIZIC LIPSĂ
                      </span>
                    )}
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold ${PRIORITY_COLORS[t.priority || "normal"]}`}>
                      {PRIORITY_LABEL[t.priority || "normal"]}
                    </span>
                    {t.due_date && (
                      <span className="text-[11px] text-zinc-600 inline-flex items-center gap-1">
                        <Clock size={11}/> Termen: {fmtRoDate(t.due_date)}
                      </span>
                    )}
                  </div>
                  <div className="font-semibold text-sm mt-1" data-testid={`task-title-${t.id}`}>
                    {(t.title || "").trim() || <span className="italic text-zinc-400">(fără titlu — task creat fără descriere; întreabă autorul)</span>}
                  </div>
                  {t.description && (t.description || "").trim() && (
                    <div className="text-xs text-zinc-700 mt-1 whitespace-pre-wrap bg-amber-50/40 border-l-2 border-amber-300 px-2 py-1 rounded-r" data-testid={`task-desc-${t.id}`}>
                      {t.description}
                    </div>
                  )}
                  <div className="text-[10px] text-zinc-500 mt-1">
                    Dat de {t.created_by_name || "—"} la {fmtRoDate((t.created_at || "").slice(0, 10))}
                  </div>
                </div>
                <Button size="sm" onClick={() => setConfirmTask(t)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`task-done-${t.id}`}>
                  <CheckCircle2 size={14} className="mr-1"/> Executat
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={!!confirmTask} onOpenChange={(o) => { if (!o) setConfirmTask(null); }}>
        <DialogContent data-testid="task-done-dialog">
          <DialogHeader><DialogTitle>Confirmi că ai executat task-ul?</DialogTitle></DialogHeader>
          <div className="text-sm">
            <div className="font-semibold mb-1">{confirmTask?.title}</div>
            {confirmTask?.description && <div className="text-xs text-zinc-600">{confirmTask.description}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmTask(null)}>Anulează</Button>
            <Button onClick={() => markDone(confirmTask)} data-testid="task-done-confirm" className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle2 size={14} className="mr-1"/> Da, am executat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
